import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ILLER } from "@/lib/iller";

const EMPTY = { title: "", customer_type: "Toptan", tax_office: "", tax_no: "", city: "", district: "", address: "", phone: "", email: "",
  contact_name: "", risk_limit: 0, vade_days: 30, sales_rep_id: "", is_active: true, phone2: "", notes: "" };
const F = ({ label, children, span }) => <div className={`space-y-1 ${span ? "col-span-2" : ""}`}><Label className="text-[11px] text-slate-500">{label}</Label>{children}</div>;

const NONE = "__none__";

export default function CustomerDialog({ customer, onClose, onSaved }) {
  const { user } = useAuth();
  const isNew = !customer?.id;
  const [f, setF] = useState(isNew ? { ...EMPTY } : Object.fromEntries(Object.keys(EMPTY).map((k) => [k, customer[k] ?? (k === "sales_rep_id" ? NONE : EMPTY[k])])));
  const [reps, setReps] = useState([]);
  const [busy, setBusy] = useState(false);
  const ownOnly = user.scope?.customers === "own" && !user.is_super;
  useEffect(() => { api.get("/customers/sales-reps").then(({ data }) => { setReps(data); if (isNew && !f.sales_rep_id) setF((p) => ({ ...p, sales_rep_id: ownOnly ? user.id : NONE })); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, risk_limit: Number(f.risk_limit), vade_days: Number(f.vade_days), sales_rep_id: f.sales_rep_id === NONE ? null : f.sales_rep_id };
    try {
      const diff = isNew ? body : Object.fromEntries(Object.entries(body).filter(([k, v]) => String(v) !== String(customer[k] ?? "")));
      const { data } = isNew ? await api.post("/customers", diff) : await api.put(`/customers/${customer.id}`, diff);
      toast.success(isNew ? "Cari oluşturuldu" : "Cari güncellendi");
      onSaved(data);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const inp = (k, props = {}) => <Input className="h-9" value={f[k]} onChange={set(k)} data-testid={`customer-form-${k}`} {...props} />;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="customer-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Yeni Cari Kartı" : `Cari Kartı · ${customer.code}`}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <F label="Firma Unvanı" span>{inp("title", { required: true })}</F>
          <F label="Cari Tipi">
            <Select value={f.customer_type} onValueChange={set("customer_type")}><SelectTrigger className="h-9" data-testid="customer-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["Toptan", "Bayi", "Perakende"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </F>
          <F label="Satış Temsilcisi">
            <Select value={f.sales_rep_id} onValueChange={set("sales_rep_id")} disabled={ownOnly}><SelectTrigger className="h-9" data-testid="customer-form-rep"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>Atanmamış</SelectItem>{reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select>
          </F>
          <F label="Vergi Dairesi">{inp("tax_office")}</F>
          <F label="Vergi / TC No (opsiyonel)">{inp("tax_no", { pattern: "\\d{10,11}", title: "10 haneli VKN veya 11 haneli TCKN" })}</F>
          <F label="Telefon">{inp("phone", { required: true })}</F>
          <F label="Telefon 2">{inp("phone2", { maxLength: 40 })}</F>
          <F label="E-posta">{inp("email", { type: "email" })}</F>
          <F label="Yetkili Kişi">{inp("contact_name")}</F>
          <F label="İl"><select required value={f.city} onChange={set("city")} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" data-testid="customer-form-city">
            <option value="">İl seçin</option>{f.city && !ILLER.includes(f.city) && <option value={f.city}>{f.city} (eski kayıt)</option>}{ILLER.map((x) => <option key={x} value={x}>{x}</option>)}</select></F>
          <F label="İlçe">{inp("district")}</F>
          <F label="Adres" span>{inp("address")}</F>
          <F label="Notlar" span><textarea className="w-full min-h-[64px] rounded-md border border-input px-3 py-2 text-sm" maxLength={2000} value={f.notes} onChange={set("notes")} data-testid="customer-form-notes" /></F>
          <F label="Risk Limiti (₺)">{inp("risk_limit", { type: "number", min: 0, step: "0.01" })}</F>
          <F label="Vade (gün)">{inp("vade_days", { type: "number", min: 0, max: 365 })}</F>
          <label className="flex items-center gap-2 text-sm col-span-2"><Switch checked={f.is_active} onCheckedChange={set("is_active")} data-testid="customer-form-active" />Aktif</label>
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="customer-form-cancel">Vazgeç</Button>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="customer-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
